import React, { useState, useEffect } from "react";
import Layout from "../../layouts";
import { eventPanelService } from "../../services/eventPanelService";
import { toast } from "react-toastify";
import DynamicTable from "../../components/DynamicTable";
import apiService from "../../services/apiService";
import { stripHtml, withSearchIcon } from "../../utils/helper";
import HtmlTooltip from "../../customComponents/HtmlTooltip";

const EVENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  { value: "Planned", label: "Planned" },
  { value: "On-Going", label: "On-Going" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Completed", label: "Completed" },
];

export default function Events() {
  const [events, setEvents] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [exportingCSV, setExportingCSV] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");

  // ✅ Copy event link
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Event link copied!");
  };

  // ✅ Open event-related admin pages
  const handleNavigate = (baseUrl: string, eventId: number | string) => {
    const ADMIN_BASE_URL =
      process.env.REACT_APP_ADMIN_PANEL_URL || "http://localhost:3000";
    const path = baseUrl.replace(ADMIN_BASE_URL + "/", "");
    const url = `${ADMIN_BASE_URL}/${path}?eventId=${eventId}`;
    console.log("url", url);
    window.open(url, "_blank");
  };

  // ✅ Updated table columns based on the new API structure
  const columns = [
    { key: "eventId", label: withSearchIcon("Event ID") },
    { key: "eventName", label: withSearchIcon("Event Name"), width: "250px" },
    { key: "hostName", label: withSearchIcon("Host Name") },
    { key: "hostAlias", label: withSearchIcon("Org Name") },
    { key: "eventMode", label: "Event Mode" },
    {
      key: "eventStatus",
      label: "Event Status",
      render: (val: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            val === "Completed"
              ? "bg-green-100 text-green-700"
              : val === "On-Going"
                ? "bg-yellow-100 text-yellow-700"
                : val === "Planned"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-gray-100 text-gray-700"
          }`}
        >
          {val}
        </span>
      ),
    },
    { key: "eventType", label: "Event Type" },
    { key: "hostUserId", label: "Host ID" },

    { key: "city", label: "City" },
    { key: "state", label: "State" },

    {
      key: "createdDate",
      label: "Create Date (Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      key: "eventStartTimeUTC",
      label: "Start Date(Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      key: "eventEndTimeUTC",
      label: "End Date(Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    { key: "eventLocation", label: "Event Location", width: "300px" },
    {
      key: "eventDescription",
      label: "Description",
      enableToolTip: false,
      render: (value?: any) => {
        const html = value || "";
        const plain = html.replace(/<[^>]+>/g, "");

        return (
          <HtmlTooltip html={html}>
            <span className="truncate inline-block max-w-[190px]">
              {plain.slice(0, 40)}...
            </span>
          </HtmlTooltip>
        );
      },
    },
    
    {
      key: "refundAllowed",
      label: "Refund Allowed",
      render: (val: boolean) => {
        console.log("refundAllowed", val);
        return val ? "Yes" : "No";
      },
    },
    {
      key: "eventLink",
      label: "Event Link",
      render: (_: any, row: any) => {
        const PINYADA_WEB_URL =
          process.env.REACT_APP_PINYADA_WEB_URL || "http://joinpionyada.dev";
        let eventLink = `${PINYADA_WEB_URL}/event-details?eventid=${row?.eventId}&hostid=${row?.hostUserId}`;
        return row?.eventId ? (
          <div className="flex gap-2">
            <button
              onClick={() => handleCopy(eventLink)}
              className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
            >
              Copy
            </button>
            <button
              onClick={() => window.open(eventLink, "_blank")}
              className="px-2 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600"
            >
              Open
            </button>
          </div>
        ) : (
          <span className="text-gray-400">No Link</span>
        );
      },
    },
    { key: "goingCount", label: "Going Count" },
    { key: "maybeCount", label: "Maybe Count" },
    { key: "notGoingCount", label: "Not Going Count" },
    { key: "eventAttendedCount", label: "Event Attended Count" },
    {
      key: "payoutStatus",
      label: "Payout Status",
      render: (val: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            val === "Not-Initiated"
              ? "bg-gray-100 text-gray-700"
              : val === "Requested"
                ? "bg-yellow-100 text-yellow-700"
                : "bg-green-100 text-green-700"
          }`}
        >
          {val || "N/A"}
        </span>
      ),
    },
    { key: "totalTickets", label: "Total Tickets" },

    { key: "totalSoldTickets", label: "Total Sold Tickets" },
    { key: "totalUnSoldTickets", label: "Total Unsold Tickets" },
    { key: "totalRefundedTickets", label: "Total Refunded Tickets" },

    {
      key: "totalSochaFees",
      label: "Total Socha Fees",
      isDecimal: true,
      isAmount: true,
    },
    {
      key: "effectiveSochaFees",
      label: "Effective Socha Fees",
      isDecimal: true,
      isAmount: true,
    },
    { key: "totalTaxesCollected", label: "Tax Amount", isDecimal: true, isAmount: true },

    {
      key: "purchasedAmount",
      label: "Purchased Amount",
      isDecimal: true,
      isAmount: true,
    },
    {
      key: "hostRevenue",
      label: "Host Revenue",
      isDecimal: true,
      isAmount: true,
    },
    { key: "requestedUserCount", label: "Requested User Count" },
    { key: "matchedUserCount", label: "Matched User Count" },
    {
      key: "showGuestlist",
      label: "Show Guestlist",
      render: (val: any) => val,
    },
    {
      key: "chatSettings",
      label: "Chat Settings",
      render: (val: any) => val,
    },
    {
      key: "actions",
      label: "Actions",
      className:
        "px-4 py-3 whitespace-nowrap text-right sticky right-0 z-10 rounded bg-white/40 backdrop-blur-md shadow-md",
      render: (_: any, row: any) => (
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            onClick={() => handleNavigate("guest-list", row.eventId)}
            className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Guests
          </button>
          <button
            onClick={() => handleNavigate("tickets-info", row.eventId)}
            className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600"
          >
            Tickets
          </button>

          <button
            onClick={() =>
              handleNavigate("events-purchased-tickets", row.eventId)
            }
            className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
          >
            Purchased
          </button>
          <button
            onClick={() => handleNavigate("refund-requests", row.eventId)}
            className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
          >
            Refunds
          </button>

          {/* {row.eventStatus === "Planned" && (
            <button
              onClick={() => {
                setSelectedEventId(row.eventId.toString());
                setShowDeleteModal(true);
              }}
              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
            >
              Cancel
            </button>
          )} */}
        </div>
      ),
    },
  ];

  // ✅ Fetch Events
  useEffect(() => {
    fetchEvents(page, searchTerm, statusFilter);
  }, [page, searchTerm, statusFilter]);

  const fetchEvents = async (
    activePageNo: number,
    searchTerm?: any,
    status: string = ""
  ) => {
    try {
      setLoading(true);
      const params: Record<string, any> = {
        page: activePageNo,
        limit: 50,
        search: searchTerm,
      };
      if (status) {
        params.status = status;
      }
      const response: any = await apiService.get("/events/get-events", params);
      setEvents(response?.data?.data || []); // ✅ updated: response.data is now event array
      setTotalRecords(response?.data?.totalCount || 0);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilterChange = (value: string) => {
    if (value === statusFilter) return;
    setStatusFilter(value);
    setPage(1);
  };

  const userId = 1; // Replace with actual logged-in user ID

  // ✅ Cancel event
  const handleDeleteEvent = async () => {
    if (!selectedEventId) return;
    try {
      const payloadData = {
        userId: Number(userId),
        eventId: Number(selectedEventId),
      };
      const res: any = await eventPanelService.deleteOrCancelEvent(payloadData);
      if (res?.status === true) {
        toast.success("The event has been cancelled successfully.");
        setEvents((prev) =>
          prev.filter((e) => e.eventId !== Number(selectedEventId)),
        );
      }
    } catch (error: any) {
      console.error("Delete error: ", error?.response?.data?.error || error);
      toast.error("Failed to cancel event. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setSelectedEventId(null);
    }
  };

  const handleExportEventsCSV = async () => {
    try {
      setExportingCSV(true);

      const exportParams = statusFilter ? { status: statusFilter } : undefined;

      const res: any = await apiService.get(
        "/export/export-events",
        exportParams,
        { responseType: "blob" }
      );

      if (res) {
        const blob =
          res instanceof Blob
            ? res
            : new Blob([res], { type: "text/csv;charset=utf-8;" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const fileName = statusFilter
          ? `${statusFilter.toLowerCase().replace(/[^a-z0-9]+/g, "_")}_events_export.csv`
          : `events_export.csv`;
        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("CSV exported successfully.");
      }
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error("Failed to export CSV. Please try again.");
    } finally {
      setExportingCSV(false);
    }
  };

  return (
    <Layout pageTitle="Events">
      <div className="min-h-screen bg-gray-50 py-3 px-4 min-w-0">
        <DynamicTable
          data={events}
          columns={columns}
          pageSize={50}
          loading={loading}
          title="Events"
          activeNUmber={page}
          onPageChange={(newPage) => setPage(newPage)}
          totalRecords={totalRecords}
          totalPages={totalPages}
          onSearch={(value: string) => {
            // 🟢 Listen to DynamicTable search input
            setPage(1); // reset to first page
            setSearchTerm(value); // update search key
          }}
          exportTypes={["csv"]}
          handleExportEventsCSV={handleExportEventsCSV}
          exportLoading={exportingCSV}
          filterSlot={
            <div className="relative">
              <label
                htmlFor="event-status-filter"
                className="absolute -top-2 left-3 px-1 bg-white text-[10px] font-semibold uppercase tracking-wide text-gray-500"
              >
                Status
              </label>
              <select
                id="event-status-filter"
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                className="appearance-none border-2 border-[#1b2431]/40 rounded-lg pl-3 pr-8 py-2 text-sm bg-white text-gray-700 shadow-sm shadow-[#1b2431]/10 hover:border-[#1b2431]/70 focus:outline-none focus:ring-2 focus:ring-[#1b2431]/30 focus:border-[#1b2431] transition cursor-pointer min-w-[140px]"
              >
                {EVENT_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value || "all"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <svg
                className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          }
        />

        {/* Cancel Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
              <h2 className="text-lg font-semibold mb-4">Cancel Event</h2>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to cancel this event? This action cannot
                be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteEvent}
                  className="px-4 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Confirm Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
